/**
 * Industry Classification Hierarchy v1.0
 * 
 * 3-tier classification system for business categorization:
 * - Primary Industry (Level 1)
 * - Sub-Category (Level 2)
 * - Detail Category (Level 3)
 */

export const industryHierarchy = {
  manufacturing: {
    label: "Manufacturing",
    subCategories: {
      discrete: {
        label: "Discrete Manufacturing",
        details: [
          { value: "automotive_parts", label: "Automotive Parts & Components" },
          { value: "machinery_equipment", label: "Machinery & Equipment" },
          { value: "electronics_assembly", label: "Electronics Assembly" },
          { value: "metal_fabrication", label: "Metal Fabrication" },
          { value: "furniture_fixtures", label: "Furniture & Fixtures" },
          { value: "other_discrete", label: "Other Discrete Manufacturing" }
        ]
      },
      process: {
        label: "Process Manufacturing",
        details: [
          { value: "food_beverage", label: "Food & Beverage Processing" },
          { value: "chemicals_plastics", label: "Chemicals & Plastics" },
          { value: "pharmaceuticals", label: "Pharmaceuticals" },
          { value: "textiles_apparel", label: "Textiles & Apparel" },
          { value: "paper_packaging", label: "Paper & Packaging" },
          { value: "other_process", label: "Other Process Manufacturing" }
        ]
      },
      custom: {
        label: "Custom / Job Shop",
        details: [
          { value: "precision_machining", label: "Precision Machining" },
          { value: "prototyping", label: "Prototyping & R&D" },
          { value: "specialty_fabrication", label: "Specialty Fabrication" },
          { value: "contract_manufacturing", label: "Contract Manufacturing" },
          { value: "other_custom", label: "Other Custom Manufacturing" }
        ]
      }
    }
  },
  retail: {
    label: "Retail",
    subCategories: {
      brick_mortar: {
        label: "Brick & Mortar Retail",
        details: [
          { value: "general_merchandise", label: "General Merchandise" },
          { value: "specialty_retail", label: "Specialty Retail" },
          { value: "grocery_supermarket", label: "Grocery & Supermarket" },
          { value: "convenience_stores", label: "Convenience Stores" },
          { value: "department_stores", label: "Department Stores" },
          { value: "other_brick_mortar", label: "Other Brick & Mortar" }
        ]
      },
      ecommerce: {
        label: "E-Commerce",
        details: [
          { value: "direct_to_consumer", label: "Direct-to-Consumer" },
          { value: "marketplace_seller", label: "Marketplace Seller" },
          { value: "subscription_commerce", label: "Subscription Commerce" },
          { value: "dropshipping", label: "Dropshipping" },
          { value: "other_ecommerce", label: "Other E-Commerce" }
        ]
      },
      omnichannel: {
        label: "Omnichannel Retail",
        details: [
          { value: "click_collect", label: "Click & Collect Operations" },
          { value: "hybrid_retail", label: "Hybrid Retail" },
          { value: "franchise_retail", label: "Franchise Retail" },
          { value: "other_omnichannel", label: "Other Omnichannel" }
        ]
      }
    }
  },
  healthcare: {
    label: "Healthcare",
    subCategories: {
      providers: {
        label: "Healthcare Providers",
        details: [
          { value: "primary_care", label: "Primary Care Practice" },
          { value: "specialty_practice", label: "Specialty Medical Practice" },
          { value: "dental_practice", label: "Dental Practice" },
          { value: "urgent_care", label: "Urgent Care / Walk-in Clinic" },
          { value: "physical_therapy", label: "Physical Therapy / Rehab" },
          { value: "mental_health", label: "Mental Health Services" },
          { value: "other_provider", label: "Other Healthcare Provider" }
        ]
      },
      facilities: {
        label: "Healthcare Facilities",
        details: [
          { value: "hospital_system", label: "Hospital / Health System" },
          { value: "surgery_center", label: "Ambulatory Surgery Center" },
          { value: "imaging_center", label: "Imaging / Diagnostic Center" },
          { value: "skilled_nursing", label: "Skilled Nursing Facility" },
          { value: "assisted_living", label: "Assisted Living / Senior Care" },
          { value: "other_facility", label: "Other Healthcare Facility" }
        ]
      },
      support: {
        label: "Healthcare Support Services",
        details: [
          { value: "medical_billing", label: "Medical Billing / Revenue Cycle" },
          { value: "healthcare_it", label: "Healthcare IT Services" },
          { value: "medical_staffing", label: "Medical Staffing" },
          { value: "lab_services", label: "Laboratory Services" },
          { value: "pharmacy_services", label: "Pharmacy Services" },
          { value: "other_support", label: "Other Support Services" }
        ]
      }
    }
  },
  professional_services: {
    label: "Professional Services",
    subCategories: {
      accounting_finance: {
        label: "Accounting & Finance",
        details: [
          { value: "cpa_firm", label: "CPA / Accounting Firm" },
          { value: "bookkeeping", label: "Bookkeeping Services" },
          { value: "tax_services", label: "Tax Preparation Services" },
          { value: "audit_services", label: "Audit & Assurance" },
          { value: "financial_advisory", label: "Financial Advisory" },
          { value: "other_accounting", label: "Other Accounting/Finance" }
        ]
      },
      legal: {
        label: "Legal Services",
        details: [
          { value: "general_practice", label: "General Practice Law Firm" },
          { value: "corporate_law", label: "Corporate / Business Law" },
          { value: "litigation", label: "Litigation / Trial Practice" },
          { value: "real_estate_law", label: "Real Estate Law" },
          { value: "family_law", label: "Family Law" },
          { value: "ip_law", label: "Intellectual Property Law" },
          { value: "other_legal", label: "Other Legal Services" }
        ]
      },
      consulting: {
        label: "Consulting",
        details: [
          { value: "management_consulting", label: "Management Consulting" },
          { value: "strategy_consulting", label: "Strategy Consulting" },
          { value: "hr_consulting", label: "HR / Organizational Consulting" },
          { value: "operations_consulting", label: "Operations Consulting" },
          { value: "marketing_consulting", label: "Marketing Consulting" },
          { value: "other_consulting", label: "Other Consulting" }
        ]
      },
      engineering: {
        label: "Engineering & Architecture",
        details: [
          { value: "civil_engineering", label: "Civil Engineering" },
          { value: "mechanical_engineering", label: "Mechanical Engineering" },
          { value: "electrical_engineering", label: "Electrical Engineering" },
          { value: "architecture", label: "Architecture" },
          { value: "environmental_engineering", label: "Environmental Engineering" },
          { value: "other_engineering", label: "Other Engineering" }
        ]
      }
    }
  },
  financial_services: {
    label: "Financial Services",
    subCategories: {
      banking: {
        label: "Banking & Lending",
        details: [
          { value: "community_bank", label: "Community Bank" },
          { value: "credit_union", label: "Credit Union" },
          { value: "mortgage_lending", label: "Mortgage Lending" },
          { value: "commercial_lending", label: "Commercial Lending" },
          { value: "consumer_finance", label: "Consumer Finance" },
          { value: "other_banking", label: "Other Banking/Lending" }
        ]
      },
      insurance: {
        label: "Insurance",
        details: [
          { value: "insurance_agency", label: "Insurance Agency / Brokerage" },
          { value: "property_casualty", label: "Property & Casualty" },
          { value: "life_health", label: "Life & Health Insurance" },
          { value: "benefits_administration", label: "Benefits Administration" },
          { value: "claims_management", label: "Claims Management" },
          { value: "other_insurance", label: "Other Insurance" }
        ]
      },
      investment: {
        label: "Investment & Wealth",
        details: [
          { value: "wealth_management", label: "Wealth Management" },
          { value: "financial_planning", label: "Financial Planning" },
          { value: "investment_advisory", label: "Investment Advisory (RIA)" },
          { value: "private_equity", label: "Private Equity / VC" },
          { value: "asset_management", label: "Asset Management" },
          { value: "other_investment", label: "Other Investment Services" }
        ]
      }
    }
  },
  technology: {
    label: "Technology",
    subCategories: {
      software: {
        label: "Software & SaaS",
        details: [
          { value: "enterprise_software", label: "Enterprise Software" },
          { value: "saas_provider", label: "SaaS Provider" },
          { value: "mobile_apps", label: "Mobile Applications" },
          { value: "custom_development", label: "Custom Software Development" },
          { value: "software_consulting", label: "Software Consulting" },
          { value: "other_software", label: "Other Software" }
        ]
      },
      it_services: {
        label: "IT Services",
        details: [
          { value: "managed_services", label: "Managed Services Provider (MSP)" },
          { value: "it_consulting", label: "IT Consulting" },
          { value: "cybersecurity", label: "Cybersecurity Services" },
          { value: "cloud_services", label: "Cloud Services" },
          { value: "data_analytics", label: "Data & Analytics Services" },
          { value: "other_it", label: "Other IT Services" }
        ]
      },
      hardware: {
        label: "Hardware & Infrastructure",
        details: [
          { value: "hardware_reseller", label: "Hardware Reseller / VAR" },
          { value: "network_infrastructure", label: "Network Infrastructure" },
          { value: "iot_devices", label: "IoT / Connected Devices" },
          { value: "telecom_services", label: "Telecommunications" },
          { value: "other_hardware", label: "Other Hardware/Infrastructure" }
        ]
      }
    }
  },
  construction: {
    label: "Construction",
    subCategories: {
      general_contracting: {
        label: "General Contracting",
        details: [
          { value: "residential_gc", label: "Residential General Contractor" },
          { value: "commercial_gc", label: "Commercial General Contractor" },
          { value: "industrial_gc", label: "Industrial Construction" },
          { value: "design_build", label: "Design-Build" },
          { value: "other_gc", label: "Other General Contracting" }
        ]
      },
      specialty_trades: {
        label: "Specialty Trades",
        details: [
          { value: "electrical", label: "Electrical Contractor" },
          { value: "plumbing_hvac", label: "Plumbing / HVAC" },
          { value: "roofing", label: "Roofing" },
          { value: "concrete_masonry", label: "Concrete / Masonry" },
          { value: "painting_finishing", label: "Painting & Finishing" },
          { value: "landscaping", label: "Landscaping / Site Work" },
          { value: "other_trade", label: "Other Specialty Trade" }
        ]
      },
      development: {
        label: "Real Estate Development",
        details: [
          { value: "residential_development", label: "Residential Development" },
          { value: "commercial_development", label: "Commercial Development" },
          { value: "land_development", label: "Land Development" },
          { value: "renovation_rehab", label: "Renovation / Rehab" },
          { value: "other_development", label: "Other Development" }
        ]
      }
    }
  },
  transportation: {
    label: "Transportation & Logistics",
    subCategories: {
      trucking: {
        label: "Trucking & Freight",
        details: [
          { value: "ltl_freight", label: "LTL Freight" },
          { value: "ftl_trucking", label: "FTL / Truckload" },
          { value: "specialized_hauling", label: "Specialized Hauling" },
          { value: "last_mile", label: "Last Mile Delivery" },
          { value: "freight_brokerage", label: "Freight Brokerage" },
          { value: "other_trucking", label: "Other Trucking" }
        ]
      },
      warehousing: {
        label: "Warehousing & Distribution",
        details: [
          { value: "general_warehousing", label: "General Warehousing" },
          { value: "fulfillment_center", label: "Fulfillment Center" },
          { value: "cold_storage", label: "Cold Storage / Refrigerated" },
          { value: "distribution_center", label: "Distribution Center" },
          { value: "other_warehousing", label: "Other Warehousing" }
        ]
      },
      specialty_transport: {
        label: "Specialty Transportation",
        details: [
          { value: "passenger_transport", label: "Passenger Transportation" },
          { value: "medical_transport", label: "Medical Transportation" },
          { value: "courier_services", label: "Courier / Express Services" },
          { value: "moving_relocation", label: "Moving & Relocation" },
          { value: "other_transport", label: "Other Transportation" }
        ]
      }
    }
  },
  hospitality: {
    label: "Hospitality",
    subCategories: {
      lodging: {
        label: "Lodging & Accommodations",
        details: [
          { value: "hotel_motel", label: "Hotel / Motel" },
          { value: "boutique_hotel", label: "Boutique Hotel" },
          { value: "resort", label: "Resort" },
          { value: "vacation_rental", label: "Vacation Rental Management" },
          { value: "bed_breakfast", label: "Bed & Breakfast / Inn" },
          { value: "other_lodging", label: "Other Lodging" }
        ]
      },
      food_beverage: {
        label: "Food & Beverage",
        details: [
          { value: "full_service_restaurant", label: "Full-Service Restaurant" },
          { value: "quick_service", label: "Quick Service / Fast Casual" },
          { value: "catering", label: "Catering Services" },
          { value: "bar_nightclub", label: "Bar / Nightclub" },
          { value: "food_truck", label: "Food Truck / Mobile" },
          { value: "other_fb", label: "Other Food & Beverage" }
        ]
      },
      events_entertainment: {
        label: "Events & Entertainment",
        details: [
          { value: "event_venue", label: "Event Venue" },
          { value: "event_planning", label: "Event Planning" },
          { value: "entertainment_venue", label: "Entertainment Venue" },
          { value: "tourism_attractions", label: "Tourism / Attractions" },
          { value: "other_events", label: "Other Events/Entertainment" }
        ]
      }
    }
  },
  other: {
    label: "Other",
    subCategories: {
      agriculture: {
        label: "Agriculture & Farming",
        details: [
          { value: "crop_production", label: "Crop Production" },
          { value: "livestock", label: "Livestock / Ranching" },
          { value: "agribusiness", label: "Agribusiness Services" },
          { value: "other_agriculture", label: "Other Agriculture" }
        ]
      },
      education: {
        label: "Education & Training",
        details: [
          { value: "private_school", label: "Private School" },
          { value: "training_provider", label: "Training Provider" },
          { value: "tutoring", label: "Tutoring Services" },
          { value: "other_education", label: "Other Education" }
        ]
      },
      nonprofit: {
        label: "Nonprofit & Social Services",
        details: [
          { value: "charitable_org", label: "Charitable Organization" },
          { value: "membership_org", label: "Membership Organization" },
          { value: "social_services", label: "Social Services" },
          { value: "other_nonprofit", label: "Other Nonprofit" }
        ]
      },
      other_industry: {
        label: "Other Industry",
        details: [
          { value: "specify_other", label: "Other (Please Specify)" }
        ]
      }
    }
  }
};

// Helper functions for working with the hierarchy
export const getPrimaryIndustries = () => {
  return Object.entries(industryHierarchy).map(([value, data]) => ({
    value,
    label: data.label
  }));
};

export const getSubCategories = (primaryIndustry) => {
  if (!primaryIndustry || !industryHierarchy[primaryIndustry]) {
    return [];
  }
  return Object.entries(industryHierarchy[primaryIndustry].subCategories).map(([value, data]) => ({
    value,
    label: data.label
  }));
};

export const getDetailCategories = (primaryIndustry, subCategory) => {
  if (!primaryIndustry || !subCategory || 
      !industryHierarchy[primaryIndustry] || 
      !industryHierarchy[primaryIndustry].subCategories[subCategory]) {
    return [];
  }
  return industryHierarchy[primaryIndustry].subCategories[subCategory].details;
};

export default industryHierarchy;
